<template>
    <tab-layout
        :buttons="buttons"
        :permissions="permissions"
        @openAddress="openAddress"
        @openAddressMap="openFullMap"
    >
        <template slot="title"> {{ $l10n.__('Adresses') }} </template>

        <template slot="content">
            <div class="relative ovy-auto">
                <preloader :display="loading" class="mt-3" />

                <div v-if="!loading && items.length > 0" class="flex-column">
                    <!-- INVOICING ADDRESS -->
                    <section v-if="mainInvoicing.length > 0" class="mb-3">
                        <h4 class="mt-0">
                            {{ $l10n.__('Adresse de facturation') }}
                            {{ sameInvoicingAndDeliveryAddress ? $l10n.__(' et livraison') : '' }}
                        </h4>

                        <address-card
                            v-for="(item, index) in mainInvoicing"
                            :key="index"
                            :data="item"
                            :permissions="permissions"
                            @emit-call="call"
                            class="well relative mb-3"
                        >
                        </address-card>
                    </section>

                    <!-- DELIVERY ADDRESS -->
                    <section
                        v-if="mainDelivery.length > 0 && !sameInvoicingAndDeliveryAddress"
                        class="mb-3"
                    >
                        <h4 class="mt-0">{{ $l10n.__('Adresse de livraison') }}</h4>

                        <address-card
                            v-for="(item, index) in mainDelivery"
                            :key="index"
                            :data="item"
                            :permissions="permissions"
                            @emit-call="call"
                            class="well relative mb-3"
                        >
                        </address-card>
                    </section>

                    <!-- OTHER ADDRESSES -->
                    <section v-if="otherAddresses.length > 0" class="mb-3">
                        <h4 class="mt-0">{{ $l10n.__('Autres adresses') }}</h4>
                        <address-card
                            v-for="(item, index) in otherAddresses"
                            :key="index"
                            :data="item"
                            :permissions="permissions"
                            @emit-call="call"
                            class="well relative mb-3"
                        >
                        </address-card>
                    </section>

                    <address-map
                        :addresses="items"
                        :corpAddress="mainCorpAddress"
                        :visible="fullMapVisible"
                        @close="closeFullMap"
                    />
                </div>

                <div v-else>
                    <div class="placeholder" v-if="!loading">
                        <p>{{ this.$l10n.__('Aucune adresse définie') }}</p>
                    </div>
                </div>
            </div>
        </template>
    </tab-layout>
</template>

<script>
import OverviewTabMixin from './OverviewTabMixin';
import { addressesActions } from './mapping/ThirdMapping';
import AddressMap from '../../AddressMap.vue';
import AddressCard from '../../AddressCard.vue';
import { hasAccess, buildUrl, formatActions } from '../../../helpers';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import TabLayout from 'vcomponents/overview/tabs/TabLayout.vue';

import { createNamespacedHelpers } from 'vuex';
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    components: {
        Preloader,
        TabLayout,
        'address-map': AddressMap,
        'address-card': AddressCard,
    },
    mixins: [OverviewTabMixin],

    props: {},

    data() {
        return {
            items: [],
            corpAddresses: [],
            permissions: {},
            actions: addressesActions,
            editMode: false,
            addressIdEdited: false,
            fullMapVisible: false,
            disableMap: true,
            loading: true,
            buttons: [], // workaround for pv5 feature detection issue SAAS-22962
        };
    },

    computed: {
        mainCorpAddress() {
            return this.corpAddresses.find((address) => address.isMainInvoicingAddress);
        },
        mainInvoicing() {
            return this.items.filter((item) => item.isMainInvoicingAddress);
        },
        mainDelivery() {
            return this.items.filter((item) => item.isMainDeliveryAddress);
        },
        otherAddresses() {
            return this.items.filter(
                (item) => !item.isMainInvoicingAddress && !item.isMainDeliveryAddress,
            );
        },
        sameInvoicingAndDeliveryAddress() {
            return (
                this.mainInvoicing.length > 0 &&
                this.mainDelivery.length > 0 &&
                this.mainInvoicing[0].id == this.mainDelivery[0].id
            );
        },
    },

    created() {
        if (this.more.buttons && this.more.buttons.length > 0) {
            this.more.buttons.map((button) => {
                button.linkedid = this.linkedid;
                button.linkedtype = this.linkedtype;
            });
        }
        this.buttons = [...this.buttons, this.more.buttons.find((button) => button.id === 'add')]; // add address is always present
        this.displaySeeMapButton();
        EventBus.$on('add-address', (item) => {
            item.actions = this.setActions(item);
            this.items.push(item);
            this.displaySeeMapButton();
        });

        EventBus.$on('edit-address', (address) => {
            address.actions = this.setActions(address);
            let index = this.items.findIndex((item) => item.id == address.id);
            this.items.splice(index, 1, address);
        });

        this.fetch();
    },

    methods: {
        ...popupPanelMapActions(['open']),
        hasAccess,
        buildUrl,
        formatActions,

        fetch() {
            this.loading = true;
            this.$ajax.get(
                `/rest/addresses/${this.linkedtype}/${this.linkedid}`,
                {},
                (response) => {
                    this.permissions = response.permissions;
                    _.each(response.items, (item) => {
                        item.actions = this.setActions(item);
                        this.items.push(item);
                    });
                    this.checkMapBtn();
                    this.corpAddresses = response.corpAddresses;
                    this.loading = false;
                },
            );
        },

        deleteItem(address) {
            this.$confirm(this.$l10n.__('Êtes-vous sûr de vouloir supprimer cette adresse ?'), '', {
                confirmButtonClass: 'btn btn-primary',
                cancelButtonClass: 'btn btn-default',
            }).then((_) => {
                this.deleteAddress(address);
            });
        },

        deleteAddress(address) {
            axios
                .delete(`/rest/addresses/delete/${address.id}`)
                .then((response) => {
                    // delete address from list
                    let index = this.items.findIndex((item) => item.id === address.id);
                    this.items.splice(index, 1);

                    // flag main invoicing address
                    if (response.data.mainAddrid !== 0) {
                        index = this.items.findIndex(
                            (item) => item.id === response.data.mainAddrid,
                        );
                        if (-1 !== index) {
                            this.items[index].isMainInvoicingAddress = true;
                            this.items[index].actions = this.setActions(this.items[index]);
                        }
                    }

                    // flag main delivery address
                    if (response.data.mainDelivAddrid !== 0) {
                        index = this.items.findIndex(
                            (item) => item.id === response.data.mainDelivAddrid,
                        );
                        if (-1 !== index) {
                            this.items[index].isMainDeliveryAddress = true;
                            this.items[index].actions = this.setActions(this.items[index]);
                        }
                    }

                    this.displaySeeMapButton();
                })
                .catch((error) =>
                    Push.error(this.$l10n.__("Problème lors de la suppression de l'adresse")),
                );
        },

        setActions(item) {
            let params = [
                {
                    key: 'id',
                    value: item.id,
                },
                {
                    key: 'linkedid',
                    value: this.linkedid,
                },
            ];
            return this.formatActions(this.actions, item, params);
        },

        checkMapBtn() {
            this.disableMap = this.items.find((item) => item.lng !== null && item.lat !== null)
                ? false
                : true;

            this.displaySeeMapButton();
        },

        openFullMap() {
            if (this.items.find((item) => item.lng !== null && item.lat !== null) ? true : false)
                this.fullMapVisible = true;
            else
                Push.error(
                    this.$l10n.__("Aucune de vos adresses n'a été géolocalisé par notre service"),
                );
        },

        closeFullMap() {
            this.fullMapVisible = false;
        },

        defineInvoicingAddress(address) {
            var self = this;
            var params = {
                action: 'updateMainAddress',
                addrid: address.id,
                linkedtype: this.linkedtype,
                linkedid: this.linkedid,
            };

            axios
                .post('/?_f=address', params)
                .then((response) => {
                    if (!(response.data.mainaddress && response.data.mainaddress.oldaddress)) {
                        return;
                    }

                    let oldInvoicingAddress = self.items.find(
                        (item) => item.id == response.data.mainaddress.oldaddress,
                    );
                    if (undefined !== oldInvoicingAddress) {
                        oldInvoicingAddress.isMainInvoicingAddress = false;
                        oldInvoicingAddress.actions = self.setActions(address);
                    }

                    address.isMainInvoicingAddress = true;
                    address.actions = self.setActions(address);
                })
                .catch((error) => {
                    Push.error(
                        this.$l10n.__(
                            'Une erreur est survenue lors de la sauvegarde. Veuillez ré-essayer.',
                        ),
                        false,
                    );
                });
        },

        defineDeliveryAddress(address) {
            var self = this;
            var params = {
                action: 'updateMainDelivAddress',
                addrid: address.id,
                linkedtype: this.linkedtype,
                linkedid: this.linkedid,
            };

            axios
                .post('/?_f=address', params)
                .then((response) => {
                    if (
                        !(
                            response.data.maindelivaddress &&
                            response.data.maindelivaddress.oldaddress
                        )
                    ) {
                        return;
                    }

                    let oldDeliveryAddress = self.items.find(
                        (item) => item.id == response.data.maindelivaddress.oldaddress,
                    );
                    if (undefined !== oldDeliveryAddress) {
                        oldDeliveryAddress.isMainDeliveryAddress = false;
                        oldDeliveryAddress.actions = self.setActions(address);
                    }

                    address.isMainDeliveryAddress = true;
                    address.actions = self.setActions(address);
                })
                .catch((error) => {
                    Push.error(
                        this.$l10n.__(
                            'Une erreur est survenue lors de la sauvegarde. Veuillez ré-essayer.',
                        ),
                        false,
                    );
                });
        },

        call(method, item) {
            if (this[method]) {
                this[method](item);
            }
        },

        openAddress(payload) {
            if (!payload.disabled) {
                this.open({
                    key: 'addressForm',
                    props: {
                        linkedid: payload.params.linkedid,
                        linkedtype: payload.params.linkedtype,
                        addressId: payload.id ? parseInt(payload.id) : false,
                    },
                });
            }
        },

        displaySeeMapButton() {
            if (this.items.length > 0) {
                const checkButtonAlreadyExist = this.more.buttons.find(
                    (button) => button.id === 'seemap',
                );
                if (!checkButtonAlreadyExist) {
                    this.more.buttons.push({
                        id: 'seemap',
                        type: 'function',
                        label: this.$l10n.__('Voir la carte'),
                        dataUpsell: 'map_listing',
                        primary: false,
                        event: 'openAddressMap',
                    });
                }
            } else {
                this.more.buttons = this.more.buttons.filter((button) => button.id !== 'seemap');
            }
            this.buttons = this.more.buttons; // update buttons with updated props
            this.$nextTick(() => {
                window.createUpsellButtons(); // update DOM after address added
            });
        },
    },
};
</script>
